'use client';

import { IconArrowNarrowRight } from '@tabler/icons-react';
import { ReleaseNoteSectionViewOf } from 'bff';
import { useTranslations } from 'i11n';
import { ReactNode } from 'react';
import { cn } from 'ui';

const Arrow = ({ label }: { label?: string }) => (
  <div
    aria-hidden
    className='flex items-center gap-1 self-center text-xs font-semibold text-primary md:flex-col md:self-auto'
  >
    {label}
    <IconArrowNarrowRight className='h-4 w-4 rotate-90 text-stone-300 md:rotate-0' />
  </div>
);

const Box = ({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) => (
  <div
    className={cn(
      'rounded-lg border border-stone-200 bg-white px-4 py-3 text-sm',
      className,
    )}
  >
    {children}
  </div>
);

// A yes/no fork drawn left to right: start, question, the two outcomes.
export const DecisionSection = ({
  section: { content },
}: {
  section: ReleaseNoteSectionViewOf<'decision'>;
}) => {
  const t = useTranslations();

  return (
    <div className='flex flex-col gap-3 md:flex-row md:items-center md:gap-4'>
      <Box className='border-foreground bg-foreground text-center font-bold text-background md:max-w-[10rem]'>
        {content.start}
      </Box>
      <Arrow />
      <Box className='border-[1.5px] border-foreground text-center text-foreground md:max-w-[13rem]'>
        {content.question}
      </Box>
      <div className='flex flex-col gap-3'>
        <div className='flex flex-col gap-2 md:flex-row md:items-center md:gap-3'>
          <Arrow label={t('release-notes.decision.yes')} />
          <Box className='border-primary bg-primary/5'>
            <p className='font-semibold text-foreground'>{content.yes.title}</p>
            <p className='text-stone-600'>{content.yes.body}</p>
          </Box>
        </div>
        <div className='flex flex-col gap-2 md:flex-row md:items-center md:gap-3'>
          <Arrow label={t('release-notes.decision.no')} />
          <Box>
            <p className='font-semibold text-foreground'>{content.no.title}</p>
            <p className='text-stone-600'>{content.no.body}</p>
            {content.no.note && (
              <p className='mt-1 text-xs font-bold text-primary'>
                {content.no.note}
              </p>
            )}
          </Box>
        </div>
      </div>
    </div>
  );
};
